import { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { capi, apiError, fileUrl, money } from "@/lib/api";
import { CustomerStatus } from "@/components/account/CustomerStatus";
import { CustomPurchase } from "@/components/account/CustomPurchase";
import { MessageThread } from "@/components/MessageThread";

function PieceList({ items = [] }) {
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <div key={i} data-testid={`piece-${i}`} className="flex gap-4 rounded-lg border border-kys-blush-border bg-white p-4">
          <div className="h-24 w-20 shrink-0 overflow-hidden rounded bg-kys-blush">
            {(it.mockup_image || it.fabric?.images?.[0] || it.style_image) && <img src={fileUrl(it.mockup_image || it.fabric?.images?.[0] || it.style_image)} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="text-sm">
            <p className="font-serif text-xl text-kys-ink">{it.style_name} <span className="font-sans text-xs text-kys-muted">· {it.category_name}</span></p>
            <p className="text-xs text-kys-charcoal">Fabric: {it.fabric?.fabric_name || "To discuss"}</p>
            <p className="text-xs text-kys-charcoal">Add-ons: {it.addons?.length ? it.addons.map((a) => a.name).join(", ") : "None"}</p>
            {it.notes && <p className="mt-1 text-xs text-kys-muted">Note: {it.notes}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function RequestDetail() {
  const { number } = useParams();
  const [r, setR] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(() => capi.get(`/account/requests/${number}`).then((res) => setR(res.data)).catch((e) => setErr(apiError(e))), [number]);
  useEffect(() => { load(); }, [load]);

  const send = async (body) => {
    await capi.post(`/account/requests/${number}/messages`, { body, origin: window.location.origin });
    await load();
  };

  if (err) return <div className="mx-auto max-w-2xl px-4 py-24 text-center"><p data-testid="request-error" className="text-sm text-kys-charcoal">{err}</p><Link to="/account" className="mt-4 inline-flex text-sm font-semibold text-kys-accent">← My Custom Orders</Link></div>;
  if (!r) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-kys-accent" /></div>;

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="request-detail-page">
      <Link to="/account" data-testid="back-to-requests" className="text-xs font-semibold text-kys-muted hover:text-kys-accent">← My Custom Orders</Link>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <h1 className="font-mono text-2xl text-kys-ink sm:text-3xl">{r.request_number}</h1>
        <CustomerStatus status={r.status} label={r.status_label} />
      </div>
      <p className="mt-1 text-xs text-kys-muted">Submitted {new Date(r.created_at).toLocaleDateString()} · Needed by {r.needed_by || "Flexible"}</p>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-3">
          {r.status === "Approved" && <CustomPurchase request={r} />}
          {r.status === "Paid" && (
            <div data-testid="paid-banner" className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 p-5 text-sm text-green-800">
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <div><p className="font-semibold">Paid — thank you!</p><p>Order {r.paid_order_number}. We'll start creating your pieces and send tracking when they ship.</p></div>
            </div>
          )}
          <PieceList items={r.items} />
          {r.general_notes && <div className="rounded-lg bg-kys-blush p-4 text-sm"><span className="text-kys-muted">Your notes:</span> {r.general_notes}</div>}
          {(r.final_price || r.quoted_price) && r.status !== "Approved" && (
            <div className="rounded-lg border border-kys-blush-border bg-white p-4 text-sm">Price: <span data-testid="request-price" className="font-mono">{money(r.final_price || r.quoted_price)}</span></div>
          )}
        </div>
        <div className="lg:col-span-2">
          <div className="rounded-lg border border-kys-blush-border bg-white p-5 lg:sticky lg:top-20">
            <h2 className="font-serif text-2xl text-kys-ink">Messages</h2>
            <p className="mb-4 text-xs text-kys-muted">Questions about fit, fabric, or timing? Message us here — we'll reply by email too.</p>
            <MessageThread messages={r.messages} onSend={send} viewer="customer" />
          </div>
        </div>
      </div>
    </div>
  );
}
